/**
 * ENG-6139 — read-only Composio connected-account binding probe.
 *
 * Verifies that a managed (Composio) integration's connected account is alive
 * AND bound to the `user_id` the agent runtime queries with. This is the signal
 * an MCP `tools/list` handshake CANNOT give: Composio returns a toolkit's tool
 * list even when no connected account exists for the entity — only tool *calls*
 * fail with `No connected account found for user ID … for toolkit …`. So a
 * dead/mis-bound account reads green on a handshake-only probe (the live
 * sherlock incident). This probe closes that gap.
 *
 * Mirrors the proven on-demand check in
 * `packages/api/src/lib/providers/composio-adapter.ts:checkConnectionDetailed`
 * + the verdict logic in `integrations.ts` (POST /integrations/:id/test), kept
 * dependency-free here so the manager-CLI host-side executor and any central
 * caller share one implementation. `fetch` is injected for unit-testing.
 *
 * Read-only: a single GET against the connected-accounts endpoint. No mutations.
 *
 * Outcome mapping (ConnectivityStatus vocabulary):
 *   - 'ok'              — account exists, ACTIVE, and bound to the expected user_id
 *   - 'down'            — not found / non-ACTIVE / bound to a different user_id
 *                         (mis-bound) / no binding returned → tool calls will fail
 *   - 'unverified'      — WE could not look: our own platform key was refused
 *                         (401/403, e.g. Composio's per-key IP allowlist). Says
 *                         nothing about the customer's account (ENG-9770)
 *   - 'transient_error' — network/timeout or 5xx → retryable, don't escalate yet
 */

import type { ConnectivityProbeOutcome } from './connectivity-probe.js';
import { assessAuthConfigLinkage } from './composio-linkage.js';
// ENG-9341: the origin is spelled in exactly one file. This module keeps its
// own injected `fetchImpl` (its `timedFetch` applies a request deadline AND a
// body-size ceiling the seam does not), so it takes the constant, not the seam.
import { COMPOSIO_API_BASE, getComposioTransport } from './composio-fetch.js';

const PROBE_TIMEOUT_MS = 10_000;

export interface ComposioAccountProbeParams {
  /** The recorded `connected_account_id` (e.g. `ca_…`). */
  connectedAccountId: string;
  /** Composio project API key sent as `x-api-key`. */
  apiKey: string;
  /** The entity the agent runtime queries with, e.g. `${orgId}:${agentId}`. */
  expectedUserId: string;
  /**
   * ENG-6157: the agent's wired MCP server id (`composio_server_id`). When
   * provided, the probe additionally asserts the account's auth_config is one
   * the server resolves with — catching the false green where the account is
   * healthy but the server points at a different auth_config. Omit to keep the
   * account-only check (ENG-6139 behaviour).
   */
  serverId?: string;
  /** Override the Composio API base (tests / self-host). */
  apiBase?: string;
}

/** Longest response body we will quote back in a message. */
const BODY_SNIPPET_MAX = 300;

/**
 * Read a short, redacted snippet of an error body for the MESSAGE only.
 *
 * Informative, never load-bearing: nothing branches on this. It exists because
 * Composio's allowlist rejection names the offending source IP, which is the
 * single most useful fact for whoever has to fix it — but the wording was never
 * recorded, so classification must not depend on it.
 *
 * The api key is redacted by exact match rather than by a general secret
 * detector: the realistic leak is the vendor echoing our own key back, and a
 * pattern-based scrubber that misses one shape is worse than a narrow rule that
 * cannot.
 */
async function readErrorBodySnippet(res: Response, apiKey: string): Promise<string | undefined> {
  // `timedFetch` has already drained this body under the request deadline and the
  // size ceiling, so this read is against an in-memory buffer and cannot stall.
  // The try/catch remains for a double that hands back an unread or consumed body:
  // diagnostics are optional, and losing them must never change the classification.
  let text: string;
  try {
    text = await res.text();
  } catch {
    return undefined;
  }
  const redacted = apiKey ? text.split(apiKey).join('«redacted»') : text;
  const trimmed = redacted.trim();
  if (!trimmed) return undefined;
  return trimmed.length > BODY_SNIPPET_MAX ? `${trimmed.slice(0, BODY_SNIPPET_MAX)}…` : trimmed;
}

/** Statuses whose bodies are null by spec — draining one is a crash, not a read. */
const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

/**
 * Hard ceiling on an error/response body, well above anything Composio returns.
 *
 * A memory bound, not a truncation: exceeding it ABORTS and throws rather than
 * handing back a half-body, because every caller here parses what it gets and a
 * silently-clipped JSON payload is a worse outcome than a probe that reports it
 * could not read the response.
 */
const BODY_MAX_BYTES = 1024 * 1024;

/**
 * Fetch under a hard deadline that covers the BODY, not just the headers.
 *
 * This is the ENG-8502 fix (CodeRabbit PR #4103), which landed on the sibling
 * `connectivity-http-probes.ts` and never on this file. `fetchImpl` resolves when
 * HEADERS arrive; clearing the abort timer there leaves every later `res.json()` /
 * `res.text()` waiting with no deadline, so a server that answers with headers and
 * then stalls the body pends the probe forever. All three body reads in this module
 * had it — the two `res.json()` calls, and the `readErrorBodySnippet` above.
 *
 * Draining here rather than at each call site is deliberate: it is the one place
 * that owns the deadline, so a future call site cannot reintroduce the hole by
 * forgetting to bound its own read.
 */
async function timedFetch(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetchImpl(url, { ...init, signal: controller.signal });
    if (NULL_BODY_STATUSES.has(res.status)) return res;
    const body = await readBoundedBody(res, controller);
    return new Response(body, {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Drain a response body, refusing to buffer more than {@link BODY_MAX_BYTES}.
 *
 * Still inside the caller's deadline, so a stalled body aborts on the timer. The
 * size ceiling is the second bound: a deadline alone lets a fast, enormous body
 * exhaust memory well before it expires. Over the ceiling we abort the request and
 * throw — callers already treat a throw as "probe failed", which is the honest
 * verdict for a response we refused to read.
 *
 * Falls back to `res.text()` when the runtime or a test double exposes no stream;
 * the deadline still applies, only the size ceiling is unavailable.
 */
async function readBoundedBody(res: Response, controller: AbortController): Promise<string> {
  const stream = res.body;
  if (!stream) return await res.text();
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    total += value.byteLength;
    if (total > BODY_MAX_BYTES) {
      controller.abort();
      await reader.cancel().catch(() => {});
      throw new Error(`Composio response body exceeded ${BODY_MAX_BYTES} bytes`);
    }
    chunks.push(value);
  }
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(merged);
}

/**
 * Probe a Composio connected account's liveness + binding.
 *
 * Returns a {@link ConnectivityProbeOutcome}; never throws (network/parse
 * failures map to `transient_error` so a blip can't flip an integration to a
 * false `down`).
 */
export async function probeComposioAccount(
  params: ComposioAccountProbeParams,
  fetchImpl?: typeof fetch,
): Promise<ConnectivityProbeOutcome> {
  // ENG-9924: this module egresses through its OWN `timedFetch`, never through
  // `composioFetch`, so the seam's transport does not reach it by inheritance —
  // it has to be asked for. That matters more here than anywhere: this is the
  // platform's Composio health monitor, and it runs in the
  // `managed-connection-health` cron, a separate Lambda with its own egress
  // identity. Leave it on direct egress and the moment ADR-0032 Part 3 arms the
  // allowlist EVERY account probe on the fleet is refused at once, reported as
  // `access_restricted` — "our key was refused" — across every row.
  //
  // An explicitly injected impl still wins, which is what keeps the tests and
  // any deliberately-unassisted caller unaffected.
  const impl = fetchImpl ?? (await getComposioTransport()) ?? fetch;
  const { connectedAccountId, apiKey, expectedUserId } = params;
  const base = params.apiBase ?? COMPOSIO_API_BASE;

  if (!connectedAccountId) {
    return {
      status: 'down',
      message: 'No connected account recorded — reconnect required',
    };
  }
  if (!apiKey || !expectedUserId) {
    // Can't perform the check honestly — treat as retryable rather than a
    // false down (a missing key/entity is a wiring problem, not a dead account).
    return {
      status: 'transient_error',
      message: 'Composio probe missing api key or expected user_id',
    };
  }

  let res: Response;
  try {
    res = await timedFetch(
      impl,
      `${base}/api/v3/connected_accounts/${encodeURIComponent(connectedAccountId)}`,
      { headers: { 'x-api-key': apiKey } },
    );
  } catch (err) {
    const isAbort = (err as Error)?.name === 'AbortError';
    return {
      status: 'transient_error',
      message: isAbort
        ? `Composio probe timed out after ${PROBE_TIMEOUT_MS / 1000}s`
        : `Composio probe failed: ${(err as Error).message}`,
    };
  }

  if (!res.ok) {
    // 5xx → retryable.
    if (res.status >= 500) {
      return {
        status: 'transient_error',
        message: `Composio unreachable (HTTP ${res.status}) — retrying`,
        details: { connectedAccountId, httpStatus: res.status },
      };
    }

    // ENG-9770 — 401/403 means OUR platform key was refused (revoked, invalid,
    // or blocked by Composio's per-key IP allowlist), so we never reached the
    // customer's account at all. This used to fall through to the `down` below
    // and tell the customer to reconnect — the one action that cannot help,
    // since their grant is fine and they have no lever on our egress. Emitted
    // on every affected row at once, because the trigger is global.
    //
    // `unverified`, not `down`: a refused read is not evidence about the
    // account. It is also the safe grade — neutral to both hysteresis counters
    // (ENG-9122), so a fleet-wide egress failure cannot mass-flip every
    // integration and mass-notify every customer.
    //
    // The discriminator is STRUCTURAL — which credential was refused — and not
    // a match on the body.
    //
    // ENG-9683 (2026-09-02) has since captured that wording verbatim, so the
    // original reason given here ("its wording was never recorded") no longer
    // holds. The decision does, for a better reason: this probe does not need
    // to know WHY our key was refused. An allowlist block, a revoked key and a
    // wrong-project key are all "we could not look", and all three must grade
    // `unverified` rather than `down`. Body markers would buy a distinction
    // nothing here acts on, at the cost of mis-grading on a vendor reword.
    // `composio-ip-protection-probe.ts` DOES need that distinction — it must
    // separate a revoked key from an allowlisted one — which is why the markers
    // live there and only there.
    //
    // The split is clean without it:
    // an expired customer grant does not 401 our api key, it 404s or returns a
    // 200 whose `status` is not ACTIVE — both handled below.
    if (res.status === 401 || res.status === 403) {
      const body = await readErrorBodySnippet(res, apiKey);
      return {
        status: 'unverified',
        cause: 'access_restricted',
        message:
          `Composio refused our API key (HTTP ${res.status}) — access restriction on our ` +
          `side, not a problem with this account; do not reconnect` +
          (body ? `. Composio said: ${body}` : ''),
        details: {
          connectedAccountId,
          httpStatus: res.status,
          ...(body ? { responseBody: body } : {}),
        },
      };
    }

    // Remaining 4xx (incl. 404 deleted/revoked) → the account really is gone.
    return {
      status: 'down',
      message: `Composio account ${connectedAccountId} not found (HTTP ${res.status}) — reconnect required`,
      details: { connectedAccountId, httpStatus: res.status },
    };
  }

  let data: { user_id?: string; status?: string; auth_config_id?: string; auth_config?: { id?: string } };
  try {
    data = (await res.json()) as {
      user_id?: string;
      status?: string;
      auth_config_id?: string;
      auth_config?: { id?: string };
    };
  } catch (err) {
    return {
      status: 'transient_error',
      message: `Composio probe response unparseable: ${(err as Error).message}`,
    };
  }

  const accountStatus = data.status ?? 'unknown';
  if (accountStatus !== 'ACTIVE') {
    return {
      status: 'down',
      message: `Composio account ${connectedAccountId} status=${accountStatus} — reconnect required`,
      details: { connectedAccountId, status: accountStatus, boundUserId: data.user_id ?? null },
    };
  }

  const boundUserId = data.user_id;
  if (!boundUserId) {
    // ACTIVE but no binding returned — can't prove the agent's query will
    // resolve. Same fail-closed stance as the Test endpoint.
    return {
      status: 'down',
      message:
        `Composio account ${connectedAccountId} is ACTIVE but returned no user_id binding — ` +
        `runtime queries as '${expectedUserId}', so tool calls can't be confirmed`,
      details: { connectedAccountId, status: accountStatus, boundUserId: null, expectedUserId },
    };
  }

  if (boundUserId !== expectedUserId) {
    return {
      status: 'down',
      message:
        `Composio account ${connectedAccountId} is bound to user_id '${boundUserId}' but the agent ` +
        `runtime queries as '${expectedUserId}' — tool calls will fail. Reconnect to bind correctly.`,
      details: { connectedAccountId, status: accountStatus, boundUserId, expectedUserId },
    };
  }

  // ENG-6157: account is ACTIVE + bound to the right entity. If the caller gave
  // us the wired server id, also assert the auth_config linkage — the account
  // can be perfectly healthy yet unreachable because the server resolves a
  // different auth_config than the account was created under.
  const accountAuthConfigId = data.auth_config_id ?? data.auth_config?.id;
  if (params.serverId) {
    const serverAuthConfigIds = await fetchServerAuthConfigIds(
      impl,
      base,
      params.serverId,
      apiKey,
    );
    const linkage = assessAuthConfigLinkage({
      accountAuthConfigId,
      serverAuthConfigIds,
      serverId: params.serverId,
    });
    if (linkage.linked === false) {
      return {
        status: 'down',
        message: linkage.message,
        details: { connectedAccountId, status: accountStatus, boundUserId, ...linkage.details },
      };
    }
  }

  return {
    status: 'ok',
    message: `Connected (account ${connectedAccountId}, status=ACTIVE)`,
    details: {
      connectedAccountId,
      status: accountStatus,
      boundUserId,
      ...(accountAuthConfigId ? { authConfigId: accountAuthConfigId } : {}),
    },
  };
}

/**
 * ENG-6157: read the auth_config ids the given MCP server resolves with.
 * Returns `null` on any fetch FAILURE (non-2xx / network / parse) so the
 * linkage check stays indeterminate rather than a false mismatch; `[]` when the
 * server genuinely binds no auth config (a real broken linkage).
 */
async function fetchServerAuthConfigIds(
  fetchImpl: typeof fetch,
  base: string,
  serverId: string,
  apiKey: string,
): Promise<string[] | null> {
  let res: Response;
  try {
    res = await timedFetch(
      fetchImpl,
      `${base}/api/v3/mcp/${encodeURIComponent(serverId)}`,
      { headers: { 'x-api-key': apiKey } },
    );
  } catch {
    return null;
  }
  if (!res.ok) return null;
  try {
    const data = (await res.json()) as {
      auth_config_ids?: string[];
      auth_configs?: Array<{ id?: string }>;
    };
    return (
      data.auth_config_ids
      ?? data.auth_configs?.map((c) => c.id).filter((id): id is string => typeof id === 'string' && id.length > 0)
      ?? []
    );
  } catch {
    return null;
  }
}
